import React from 'react';
import SectionCard from '../../components/properties/controls/SectionCard';
import Slider from '../../components/properties/controls/Slider';
import Button from '../../components/properties/controls/Button';
import NumberInput from '../../components/properties/controls/NumberInput';
import Toggle from '../../components/properties/controls/Toggle';
import { FILTER_PRESETS, getPresetAdjustments } from '../../utils/imageFilterUtils';
import styles from './ImageToolPanel.module.css';

const ImageEditPanel = ({ model, disabled }) => {
  const brightness = model.mixed('adjust.brightness');
  const contrast = model.mixed('adjust.contrast');
  const saturation = model.mixed('adjust.saturation');
  const warmth = model.mixed('adjust.warmth');
  const blur = model.mixed('adjust.blur');
  const temperature = model.mixed('adjust.temperature');
  const tint = model.mixed('adjust.tint');
  const highlights = model.mixed('adjust.highlights');
  const shadows = model.mixed('adjust.shadows');
  const whites = model.mixed('adjust.whites');
  const blacks = model.mixed('adjust.blacks');
  const vibrance = model.mixed('adjust.vibrance');
  const invert = model.mixed('adjust.invert');
  const sharpness = model.mixed('adjust.sharpness');
  const clarity = model.mixed('adjust.clarity');
  const vignette = model.mixed('adjust.vignette');
  const preset = model.mixed('filterPreset');

  const handleNumericCommit = (path, value, reason) => {
    model.applyToSelection({ adjust: { [path]: value } }, 'immediate', reason);
  };

  const renderAdjustRow = ({
    label,
    value,
    min,
    max,
    step,
    path,
    reason,
  }) => (
    <div className={styles.adjustRow}>
      <div className={styles.adjustLabel}>{label}</div>
      <div className={styles.adjustControls}>
        <Slider
          min={min}
          max={max}
          step={step}
          value={value.kind === 'mixed' ? (min + max) / 2 : value.value}
          mixed={value.kind === 'mixed'}
          onChange={(next) =>
            model.applyToSelection(
              { adjust: { [path]: next } },
              'commitOnRelease',
              reason,
            )
          }
          onCommit={() => model.commitPending(reason)}
          disabled={disabled}
          showValue={false}
        />
        <NumberInput
          value={value.kind === 'mixed' ? undefined : value.value}
          mixed={value.kind === 'mixed'}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onCommit={(next) => handleNumericCommit(path, next, reason)}
        />
      </div>
    </div>
  );

  const resetAdjustments = () => {
    model.applyToSelection(
      {
        adjust: {
          brightness: 1,
          contrast: 1,
          saturation: 1,
          warmth: 0,
          blur: 0,
          temperature: 0,
          tint: 0,
          highlights: 0,
          shadows: 0,
          whites: 0,
          blacks: 0,
          vibrance: 0,
          invert: false,
          sharpness: 0,
          clarity: 0,
          vignette: 0,
        },
        filterPreset: 'none',
      },
      'immediate',
      'adjust-reset',
    );
  };

  return (
    <div className={styles.toolContent}>
      <SectionCard title="Adjust">
        <div className={styles.sectionGroup}>
          <div className={styles.sectionTitle}>White balance</div>
          {renderAdjustRow({
            label: 'Temperature',
            value: temperature,
            min: -100,
            max: 100,
            step: 1,
            path: 'temperature',
            reason: 'adjust-temperature',
          })}
          {renderAdjustRow({
            label: 'Tint',
            value: tint,
            min: -100,
            max: 100,
            step: 1,
            path: 'tint',
            reason: 'adjust-tint',
          })}
          {renderAdjustRow({
            label: 'Warmth',
            value: warmth,
            min: -100,
            max: 100,
            step: 1,
            path: 'warmth',
            reason: 'adjust-warmth',
          })}
        </div>

        <div className={styles.sectionGroup}>
          <div className={styles.sectionTitle}>Light</div>
          {renderAdjustRow({
            label: 'Brightness',
            value: brightness,
            min: 0,
            max: 2,
            step: 0.01,
            path: 'brightness',
            reason: 'adjust-brightness',
          })}
          {renderAdjustRow({
            label: 'Contrast',
            value: contrast,
            min: 0,
            max: 2,
            step: 0.01,
            path: 'contrast',
            reason: 'adjust-contrast',
          })}
          {renderAdjustRow({
            label: 'Highlights',
            value: highlights,
            min: -100,
            max: 100,
            step: 1,
            path: 'highlights',
            reason: 'adjust-highlights',
          })}
          {renderAdjustRow({
            label: 'Shadows',
            value: shadows,
            min: -100,
            max: 100,
            step: 1,
            path: 'shadows',
            reason: 'adjust-shadows',
          })}
          {renderAdjustRow({
            label: 'Whites',
            value: whites,
            min: -100,
            max: 100,
            step: 1,
            path: 'whites',
            reason: 'adjust-whites',
          })}
          {renderAdjustRow({
            label: 'Blacks',
            value: blacks,
            min: -100,
            max: 100,
            step: 1,
            path: 'blacks',
            reason: 'adjust-blacks',
          })}
        </div>

        <div className={styles.sectionGroup}>
          <div className={styles.sectionTitle}>Colour</div>
          <div className={styles.adjustRow}>
            <div className={styles.adjustLabel}>Invert</div>
            <div className={styles.adjustControls}>
              <Toggle
                checked={invert.kind === 'mixed' ? false : Boolean(invert.value)}
                indeterminate={invert.kind === 'mixed'}
                onChange={(checked) =>
                  model.applyToSelection(
                    { adjust: { invert: checked } },
                    'immediate',
                    'adjust-invert',
                  )
                }
                disabled={disabled}
              />
            </div>
          </div>
          {renderAdjustRow({
            label: 'Vibrance',
            value: vibrance,
            min: -100,
            max: 100,
            step: 1,
            path: 'vibrance',
            reason: 'adjust-vibrance',
          })}
          {renderAdjustRow({
            label: 'Saturation',
            value: saturation,
            min: 0,
            max: 2,
            step: 0.01,
            path: 'saturation',
            reason: 'adjust-saturation',
          })}
        </div>

        <div className={styles.sectionGroup}>
          <div className={styles.sectionTitle}>Texture</div>
          {renderAdjustRow({
            label: 'Sharpness',
            value: sharpness,
            min: 0,
            max: 100,
            step: 1,
            path: 'sharpness',
            reason: 'adjust-sharpness',
          })}
          {renderAdjustRow({
            label: 'Clarity',
            value: clarity,
            min: -100,
            max: 100,
            step: 1,
            path: 'clarity',
            reason: 'adjust-clarity',
          })}
          {renderAdjustRow({
            label: 'Vignette',
            value: vignette,
            min: -100,
            max: 100,
            step: 1,
            path: 'vignette',
            reason: 'adjust-vignette',
          })}
          {renderAdjustRow({
            label: 'Blur',
            value: blur,
            min: 0,
            max: 20,
            step: 0.5,
            path: 'blur',
            reason: 'adjust-blur',
          })}
        </div>
        <Button onClick={resetAdjustments} disabled={disabled}>
          Reset edits
        </Button>
      </SectionCard>

      <SectionCard title="Filters">
        <div className={styles.filterGrid}>
          {Object.entries(FILTER_PRESETS).map(([key, entry]) => (
            <button
              type="button"
              key={key}
              className={`${styles.filterButton} ${
                preset.kind === 'single' && preset.value === key ? styles.filterButtonActive : ''
              }`}
              onClick={() => {
                const adjustments = getPresetAdjustments(key);
                model.applyToSelection(
                  { adjust: adjustments, filterPreset: key },
                  'debounced',
                  `filter-${key}`,
                );
              }}
              disabled={disabled}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

export default ImageEditPanel;
